/**
 * Module: screens/creator/_AudienceEditor
 * Role: Presentational editor for the content part of the creator profile
 *       (categories / formats / audience / cities) — FTR-1458 доводка PATCH.
 *       Сети не трогает: родитель (ProfileEditScreen) владеет useQuery/useMutation
 *       и передаёт значения и колбэк вниз. Подчёркивание в имени = приватный
 *       компонент, не цель маршрута.
 *
 *       Продуктовый инвариант: кнопка «Сохранить» активна только когда что-то
 *       реально изменилось. PATCH шлёт ТОЛЬКО изменённые поля — сервер не
 *       затирает то, чего мы не трогали, и человек не теряет данные мастера
 *       онбординга, открыв этот экран.
 *
 *       Составы вариантов приходят из `GET /creator/options` (бэкенд — SSOT):
 *       второй копии enum'ов здесь нет. Русские подписи — `_labels.ts`.
 *
 * Uses: api/creator:CreatorProfileUpdate; ./_labels; ./steps/_ProfileChips:Chips;
 *       ./useCreatorOptions; styles/tokens; components/atoms
 * Used by: screens/creator/ProfileEditScreen.tsx
 * ADR-023: ни одного window.Telegram / литерала платформы вне src/platform/
 * Glossary: ai/glossary/buyer.md
 */
import { useMemo, useState } from 'react';
import { P, S, T } from '../../styles/tokens';
import { Card, Input, PressBtn } from '../../components/atoms';
import type { CreatorProfileUpdate } from '../../api/creator';
import { Chips } from './steps/_ProfileChips';
import { ageRangeLabel, categoryLabel, formatLabel, genderLabel } from './_labels';
import { useCreatorOptions } from './useCreatorOptions';

/** Города быстрого выбора — тот же список, что в мастере (CitiesStep). */
const POPULAR_CITIES = [
  'Москва',
  'Санкт-Петербург',
  'Новосибирск',
  'Екатеринбург',
  'Казань',
  'Нижний Новгород',
  'Краснодар',
  'Сочи',
];

/** Профиль приходит с бэкенда как свободный dict — читаем защитно (ADR-015). */
function readList(profile: Record<string, unknown> | null | undefined, key: string): string[] {
  const value = profile?.[key];
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

function readString(profile: Record<string, unknown> | null | undefined, key: string): string {
  const value = profile?.[key];
  return typeof value === 'string' ? value : '';
}

function sameList(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i]);
}

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ font: T.subhead, fontWeight: 600, color: P.ink, marginTop: 18 }}>{children}</div>
  );
}

/** Одиночный выбор поверх той же формы чипов: повторный тап снимает значение. */
function SingleChips({
  items,
  selected,
  onPick,
}: {
  items: { value: string; label: string }[];
  selected: string;
  onPick(value: string): void;
}) {
  return (
    <Chips
      items={items}
      selected={selected ? [selected] : []}
      onToggle={(v) => onPick(v === selected ? '' : v)}
    />
  );
}

export interface AudienceEditorProps {
  profile: Record<string, unknown> | null;
  busy: boolean;
  error: string | null;
  onSave(patch: CreatorProfileUpdate): void;
}

export function AudienceEditor({ profile, busy, error, onSave }: AudienceEditorProps) {
  const initialCategories = readList(profile, 'content_categories');
  const initialFormats = readList(profile, 'ugc_formats');
  const initialGender = readString(profile, 'audience_gender');
  const initialAgeRange = readString(profile, 'audience_age_range');
  const initialCities = readList(profile, 'audience_top_cities');

  const [categories, setCategories] = useState<string[]>(initialCategories);
  const [formats, setFormats] = useState<string[]>(initialFormats);
  const [gender, setGender] = useState<string>(initialGender);
  const [ageRange, setAgeRange] = useState<string>(initialAgeRange);
  const [cities, setCities] = useState<string[]>(initialCities);
  const [customCity, setCustomCity] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);

  const { options, isLoading } = useCreatorOptions();
  const limits = options?.limits ?? { max_formats: 5, max_cities: 3, city_max_chars: 50 };

  const categoryItems = useMemo(
    () => (options?.categories ?? []).map((v) => ({ value: v, label: categoryLabel(v) })),
    [options?.categories],
  );
  const formatItems = useMemo(
    () => (options?.formats ?? []).map((v) => ({ value: v, label: formatLabel(v) })),
    [options?.formats],
  );
  const genderItems = useMemo(
    () => (options?.genders ?? []).map((v) => ({ value: v, label: genderLabel(v) })),
    [options?.genders],
  );
  const ageItems = useMemo(
    () => (options?.age_ranges ?? []).map((v) => ({ value: v, label: ageRangeLabel(v) })),
    [options?.age_ranges],
  );

  const cityChoices = useMemo(
    () => Array.from(new Set([...POPULAR_CITIES, ...initialCities, ...cities])),
    [initialCities, cities],
  );

  function toggle(list: string[], value: string, max?: number): string[] {
    if (list.includes(value)) return list.filter((v) => v !== value);
    if (max !== undefined && list.length >= max) return list;
    return [...list, value];
  }

  function addCustomCity() {
    const city = customCity.trim();
    if (!city) return;
    if (city.length > limits.city_max_chars) {
      setLocalError(`Название города не длиннее ${limits.city_max_chars} символов`);
      return;
    }
    if (cities.length >= limits.max_cities || cities.includes(city)) return;
    setLocalError(null);
    setCities((prev) => [...prev, city]);
    setCustomCity('');
  }

  // PATCH шлёт ТОЛЬКО изменившиеся поля — остальное сервер не трогает.
  const patch: CreatorProfileUpdate = {};
  if (!sameList(categories, initialCategories)) patch.categories = categories;
  if (!sameList(formats, initialFormats)) patch.formats = formats;
  if (gender !== initialGender && gender) patch.gender = gender;
  if (ageRange !== initialAgeRange && ageRange) patch.age_range = ageRange;
  if (!sameList(cities, initialCities)) patch.cities = cities;
  const dirty = Object.keys(patch).length > 0;

  return (
    <Card style={{ marginTop: 16, padding: 16 }}>
      <div style={{ font: T.bodyText, fontWeight: 600, color: P.ink }}>Аудитория</div>
      <div style={{ font: T.footnote, color: S.secondaryLabel, marginTop: 4 }}>
        По этим данным селлеры подбирают креаторов под свой товар
      </div>

      {isLoading && (
        <div style={{ font: T.footnote, color: P.inkSoft, marginTop: 12 }}>Загружаем варианты…</div>
      )}

      {!isLoading && (
        <>
          <SectionTitle>Категории</SectionTitle>
          <Chips
            items={categoryItems}
            selected={categories}
            onToggle={(v) => setCategories((prev) => toggle(prev, v))}
          />

          <SectionTitle>Форматы — не больше {limits.max_formats}</SectionTitle>
          <Chips
            items={formatItems}
            selected={formats}
            max={limits.max_formats}
            onToggle={(v) => setFormats((prev) => toggle(prev, v, limits.max_formats))}
          />

          <SectionTitle>Пол аудитории</SectionTitle>
          <SingleChips items={genderItems} selected={gender} onPick={setGender} />

          <SectionTitle>Возраст аудитории</SectionTitle>
          <SingleChips items={ageItems} selected={ageRange} onPick={setAgeRange} />

          <SectionTitle>Города — не больше {limits.max_cities}</SectionTitle>
          <Chips
            items={cityChoices.map((c) => ({ value: c, label: c }))}
            selected={cities}
            max={limits.max_cities}
            onToggle={(v) => setCities((prev) => toggle(prev, v, limits.max_cities))}
          />
          <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <Input value={customCity} onChange={setCustomCity} placeholder="Другой город" />
            </div>
            <PressBtn
              onClick={addCustomCity}
              disabled={cities.length >= limits.max_cities || !customCity.trim()}
              bg={P.ink}
              fg="#ffffff"
              full={false}
              size="md"
              style={{ flexShrink: 0, paddingLeft: 20, paddingRight: 20 }}
            >
              Добавить
            </PressBtn>
          </div>
        </>
      )}

      {(localError || error) && (
        <div style={{ font: T.footnote, color: P.danger, marginTop: 12 }}>
          {localError ?? error}
        </div>
      )}

      <PressBtn
        onClick={() => onSave(patch)}
        disabled={!dirty || busy || isLoading}
        size="md"
        style={{ marginTop: 16 }}
      >
        {busy ? 'Сохраняем…' : 'Сохранить'}
      </PressBtn>
    </Card>
  );
}
